import React, {useEffect, useRef, useState} from "react";
import Lottie from "lottie-react";
import ClickAnimation from "../assets/json/ClickAnimation.json";
import AngasAudio from "../assets/sounds/Angas.mp3";
import DiskarteAudio from "../assets/sounds/Diskarte.mp3";
import KulitAudio from "../assets/sounds/Kulit.mp3";
import KusaAudio from "../assets/sounds/Kusa.mp3";
import PusoAudio from "../assets/sounds/Puso.mp3";
import TalinoAudio from "../assets/sounds/Talino.mp3";
import TiyagaAudio from "../assets/sounds/Tiyaga.mp3";

type Area = {
  shape: string;
  coords: string;
  alt: string;
  color?: string;
  className?: string;
};

type ResponsiveImageMapProps = {
  src: string;
  alt: string;
  mapName: string;
  areas: Area[];
};

const ResponsiveImageMapDNA: React.FC<ResponsiveImageMapProps> = ({
  src,
  alt,
  mapName,
  areas,
}) => {
  const audioRefs = useRef({
    angas: new Audio(AngasAudio),
    diskarte: new Audio(DiskarteAudio),
    kulit: new Audio(KulitAudio),
    kusa: new Audio(KusaAudio),
    puso: new Audio(PusoAudio),
    talino: new Audio(TalinoAudio),
    tiyaga: new Audio(TiyagaAudio),
  });

  const imgRef = useRef<HTMLImageElement | null>(null);
  const [scaledAreas, setScaledAreas] = useState<Area[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null); // Track highlighted area index

  const [audioLoading, setAudioLoading] = useState(false);

  const clickSpeakHandler = (audioKey: keyof typeof audioRefs.current) => {
    const audio = audioRefs.current[audioKey];
    setAudioLoading(true);

    // Stop and reset all other audio instances
    Object.values(audioRefs.current).forEach((audioInstance) => {
      audioInstance.pause();
      audioInstance.currentTime = 0;
    });

    // Declare the interval variable
    let interval: NodeJS.Timeout;

    // Function to check if the audio has ended
    const checkIfEnded = () => {
      if (Math.abs(audio.currentTime - audio.duration) < 0.1) {
        setAudioLoading(false); // Set loading to false when the audio ends
        clearInterval(interval); // Stop the interval
      }
    };

    if (audio.paused) {
      // Start playing and set an interval to monitor the playback status
      audio.play().catch((error) => {
        console.error("Audio playback failed:", error);
        setAudioLoading(false); // Reset loading state if playback fails
      });

      interval = setInterval(checkIfEnded, 100); // Check every 100ms
    } else {
      // Pause and reset the audio
      audio.pause();
      audio.currentTime = 0;
      setAudioLoading(false); // Immediately stop loading
    }
  };

  useEffect(() => {
    const resizeMap = () => {
      if (!imgRef.current) return;

      const img = imgRef.current;
      if (!img.naturalWidth || !img.naturalHeight) return;

      const originalWidth = img.naturalWidth;
      const originalHeight = img.naturalHeight;

      const currentWidth = img.offsetWidth;
      const currentHeight = img.offsetHeight;

      const xRatio = currentWidth / originalWidth;
      const yRatio = currentHeight / originalHeight;

      const updatedAreas = areas.map((area) => {
        const coords = area.coords.split(",").map(Number);
        const scaledCoords = coords.map((coord, index) =>
          index % 2 === 0 ? coord * xRatio : coord * yRatio
        );
        return {...area, coords: scaledCoords.join(",")};
      });

      setScaledAreas(updatedAreas);
    };

    const handleResize = () => resizeMap();

    window.addEventListener("resize", handleResize);

    const img = imgRef.current;
    if (img && img.complete) {
      resizeMap();
    } else {
      img?.addEventListener("load", resizeMap);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [areas]);

  const renderBackgrounds = () => {
    return scaledAreas.map((area, index) => {
      const coords = area.coords.split(",").map(Number);
      let style: React.CSSProperties = {};

      if (area.shape === "rect") {
        style = {
          left: `${coords[0]}px`,
          top: `${coords[1]}px`,
          width: `${coords[2] - coords[0]}px`,
          height: `${coords[3] - coords[1]}px`,
        };
      } else if (area.shape === "circle") {
        style = {
          left: `${coords[0] - coords[2]}px`,
          top: `${coords[1] - coords[2]}px`,
          width: `${coords[2] * 2}px`,
          height: `${coords[2] * 2}px`,
          borderRadius: "50%",
        };
      }

      const isHighlighted = index === highlightedIndex;

      return (
        <div
          className={`parent-modal-bubble-dna`}
          key={index}
          onMouseEnter={(e) => {
            if (index === 3) {
              let cursorPointer = document.getElementById("cursorPointer");
              if (cursorPointer) {
                cursorPointer.style.display = "none";
              }
            }
          }}
          style={{
            ...style,
            position: "absolute",
            backgroundColor: isHighlighted
              ? "rgba(0, 255, 0, 0.5)"
              : area.color || "rgba(255, 0, 0, 0.5)",
            cursor: "pointer",
          }}>
          {index === 3 && (
            <Lottie
              id="cursorPointer"
              animationData={ClickAnimation}
              className="click-animation-blk-dna"
            />
          )}
          {area.alt === "Angas" && (
            <div
              className={`modal-bubble-dna ${area.alt.toLocaleLowerCase()}-bubble-blk`}>
              <p>
                Angas [AHNG-us]
                <i
                  className="fa-solid fa-volume-high"
                  onClick={() => clickSpeakHandler("angas")}></i>
                <span>{audioLoading ? "Playing..." : null}</span>
              </p>
              <p>(Self-Assured Confidence)</p>
              <p>
                Confidence from knowing one's abilities and worth. Commanding
                attention and respect with boldness and assertiveness.
              </p>
              <p>Angas in Action:</p>
              <ul>
                <li>
                  <p>
                    Boldly speaks up and contributes constructively to
                    discussions.
                  </p>
                </li>
                <li>
                  <p>Embraces challenges and steps out of comfort zone.</p>
                </li>
                <li>
                  <p>Takes ownership of tasks and projects</p>
                </li>
                <li>
                  <p>Communicates clearly and directly</p>
                </li>
                <li>
                  <p>
                    Demonstrates confidence and self-awareness by acknowledging
                    both strengths and vulnerabilities. Seeks help when
                    necessary.
                  </p>
                </li>
              </ul>
            </div>
          )}
          {area.alt === "Diskarte" && (
            <div
              className={`modal-bubble-dna ${area.alt.toLocaleLowerCase()}-bubble-blk`}>
              <p>
                Diskarte [dees-KAR-teh]
                <i
                  className="fa-solid fa-volume-high"
                  onClick={() => clickSpeakHandler("diskarte")}></i>
                <span>{audioLoading ? "Playing..." : null}</span>
              </p>
              <p>(Resourceful & Practical Ingenuity)</p>
              <p>
                Diskarte is our embodiment of resourcefulness and practical
                wisdom. It's about employing creative strategies and a
                street-smart approach to problem-solving. This value reflects
                our ability to adapt, think quickly, and navigate challenges
                effectively.
              </p>
              <p>Diskarte in Action:</p>
              <ul>
                <li>
                  <p>
                    Adaptability and Flexibility: We show adaptability and
                    flexibility in various situations, ensuring we meet our
                    objectives even in changing circumstances.
                  </p>
                </li>
                <li>
                  <p>
                    Proactive Planning: We use foresight and intuition to
                    anticipate consequences and plan ahead, ensuring smooth
                    operations.{" "}
                  </p>
                </li>
                <li>
                  <p>
                    Innovative Problem-Solving: Our resourcefulness allows us to
                    navigate challenges with limited options, using
                    unconventional approaches to achieve goals.
                  </p>
                </li>
                <li>
                  <p>
                    Building Relationships: We effectively build rapport and
                    networks, leveraging these relationships to achieve our
                    objectives.
                  </p>
                </li>
              </ul>
            </div>
          )}
          {area.alt === "Talino" && (
            <div
              className={`modal-bubble-dna ${area.alt.toLocaleLowerCase()}-bubble-blk`}>
              <p>
                Talino [tah-LEE-noh]
                <i
                  className="fa-solid fa-volume-high"
                  onClick={() => clickSpeakHandler("talino")}></i>
                <span>{audioLoading ? "Playing..." : null}</span>
              </p>
              <p>(Cognitive Agility)</p>
              <p>
                Talino represents our combination of cognitive agility, depth of
                expertise, and the ability to learn quickly. It's about
                problem-solving, critical thinking, and the ability to grasp
                complex concepts efficiently.
              </p>
              <p>Talino in Action:</p>
              <ul>
                <li>
                  <p>
                    Critical Thinking: We analyse information critically,
                    identify key issues, and make sound judgments, ensuring
                    informed decisions.
                  </p>
                </li>
                <li>
                  <p>
                    Quick Learning: Our team is quick to grasp instructions and
                    retain information, enabling us to stay ahead in our field.
                  </p>
                </li>
                <li>
                  <p>
                    Continuous Improvement: We continuously pursue opportunities
                    to expand our expertise, staying updated on industry trends
                    and best practices.
                  </p>
                </li>
                <li>
                  <p>
                    Effective Communication: We leverage new insights to
                    innovate and communicate substantive ideas clearly and
                    persuasively.
                  </p>
                </li>
              </ul>
            </div>
          )}
          {area.alt === "Tiyaga" && (
            <div
              className={`modal-bubble-dna ${area.alt.toLocaleLowerCase()}-bubble-blk`}>
              <p>
                Tiyaga: [cha-GAH]
                <i
                  className="fa-solid fa-volume-high"
                  onClick={() => clickSpeakHandler("tiyaga")}></i>
                <span>{audioLoading ? "Playing..." : null}</span>
              </p>
              <p>(Grit & Tenacity)</p>
              <p>
                Tiyaga embodies the grit, tenacity, and patience crucial for
                achieving long-term goals. It's about staying composed in
                adversity and being committed to completing projects, no matter
                how challenging.
              </p>
              <p>Tiyaga in Action:</p>
              <ul>
                <li>
                  <p>
                    Long-Term Focus: We maintain focus on long-term goals
                    despite short-term setbacks, ensuring progress is steady and
                    consistent.
                  </p>
                </li>
                <li>
                  <p>
                    Diligence: Our team demonstrates diligence and reliability
                    in completing tasks and tackling challenges persistently.
                  </p>
                </li>
                <li>
                  <p>
                    Attention to Detail: We pay meticulous attention to detail,
                    ensuring thorough understanding and consideration of all
                    factors in our work.
                  </p>
                </li>
                <li>
                  <p>
                    Composure Under Pressure: We remain composed and
                    level-headed under pressure, ensuring that our performance
                    remains consistent and high-quality.
                  </p>
                </li>
              </ul>
            </div>
          )}
          {area.alt === "Kusa" && (
            <div
              className={`modal-bubble-dna ${area.alt.toLocaleLowerCase()}-bubble-blk`}>
              <p>
                Kusa: [KOO-sah]
                <i
                  className="fa-solid fa-volume-high"
                  onClick={() => clickSpeakHandler("kusa")}></i>
                <span>{audioLoading ? "Playing..." : null}</span>
              </p>
              <p>(Independent Initiative)</p>
              <p>
                Kusa reflects our self-initiative and taking proactive action
                independently. Reflects a sense of self- motivation, personal
                responsibility, and the drive to contribute positively and make
                a difference.
              </p>
              <p>Kusa in Action:</p>
              <ul>
                <li>
                  <p>
                    Proactive Contribution: We take the initiative to act
                    independently and responsibly, actively seeking ways to add
                    value.
                  </p>
                </li>
                <li>
                  <p>
                    Ownership of Tasks: Our team takes ownership of tasks and
                    responsibilities, ensuring that projects are completed
                    efficiently and effectively.
                  </p>
                </li>
                <li>
                  <p>
                    Active Involvement: We identify areas where our skills can
                    contribute and involve ourselves in relevant projects,
                    making a positive impact.
                  </p>
                </li>
                <li>
                  <p>
                    Responsibility: We act willingly and proactively, without
                    the need for reminders, in pursuit of organisational goals.
                  </p>
                </li>
              </ul>
            </div>
          )}
          {area.alt === "Kulit" && (
            <div
              className={`modal-bubble-dna ${area.alt.toLocaleLowerCase()}-bubble-blk`}>
              <p>
                Kulit: [koo-LEET]
                <i
                  className="fa-solid fa-volume-high"
                  onClick={() => clickSpeakHandler("kulit")}></i>
                <span>{audioLoading ? "Playing..." : null}</span>
              </p>
              <p>(Cheerful Persistence & Curiosity)</p>
              <p>
                Kulit is our insatiable curiosity and relentless pursuit of
                answers. It's about maintaining a playful persistence and
                spreading positivity, even in the face of setbacks.
              </p>
              <p>Kulit in Action:</p>
              <ul>
                <li>
                  <p>
                    Playful Curiosity: We approach tasks with curiosity and an
                    innovative mindset, generating fresh ideas and solutions.
                  </p>
                </li>
                <li>
                  <p>
                    Relentless Pursuit: Our team fearlessly pursues desired
                    outcomes, persisting until resolution is achieved.
                  </p>
                </li>
                <li>
                  <p>
                    Questioning Norms: We encourage creativity by questioning
                    established norms and practices, sparking innovation.
                  </p>
                </li>
                <li>
                  <p>
                    Resilience and Positivity: We bounce back from setbacks with
                    resilience, maintaining a cheerful demeanour and inspiring
                    others to do the same
                  </p>
                </li>
              </ul>
            </div>
          )}
          {area.alt === "Puso" && (
            <div
              className={`modal-bubble-dna ${area.alt.toLocaleLowerCase()}-bubble-blk`}>
              <p>
                Puso: [POO-soh]
                <i
                  className="fa-solid fa-volume-high"
                  onClick={() => clickSpeakHandler("puso")}></i>
                <span>{audioLoading ? "Playing..." : null}</span>
              </p>
              <p>(Heart, Passion, & Purpose)</p>
              <p>
                Puso is the heart of iCXeed, embodying our inner strength,
                passion, and commitment to excellence. It's about leading with
                empathy, purpose, and resilience.
              </p>
              <p>Puso in Action:</p>
              <ul>
                <li>
                  <p>
                    Empathy and Connection: We demonstrate empathy by
                    considering others' viewpoints and experiences in our
                    decisions, fostering strong connections.
                  </p>
                </li>
                <li>
                  <p>
                    Passionate Leadership: Our team drives the relentless
                    pursuit of goals, facing challenges with courage and
                    embracing learning opportunities.
                  </p>
                </li>
                <li>
                  <p>
                    Dedication to Excellence: We are committed to achieving
                    excellence in all our work, contributing to the success of
                    the organisation
                  </p>
                </li>
                <li>
                  <p>
                    Resilience: We lead by example, showing inner strength and
                    resilience in all our actions and decisions.
                  </p>
                </li>
              </ul>
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div style={{position: "relative"}}>
      <img
        ref={imgRef}
        src={src}
        useMap={`#${mapName}`}
        alt={alt}
        style={{maxWidth: "100%", height: "auto", display: "block"}}
      />
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}>
        {renderBackgrounds()}
      </div>
    </div>
  );
};

export default ResponsiveImageMapDNA;
